import { useRef } from 'react';
import Alert from 'react-bootstrap/Alert';
import AppButton from '../atoms/AppButton';
import AppSpinner from '../atoms/AppSpinner';
import { IconImage } from '../atoms/Icon';
import useImageUpload from '../hooks/useImageUpload';
import { useIsTouch } from '../hooks/useMediaQuery';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
// Banner é o único upload cujo backend aceita vídeo além de imagem/GIF.
const ACCEPT_WIDE = `${ACCEPT},video/mp4,video/webm`;

// Confere pela extensão da URL salva, não pelo `File` (que só existe antes do
// upload) — depois de salvo só temos a URL vinda do backend, e é ela que
// decide se a prévia é `<img>` ou `<video>`.
const VIDEO_EXT_RE = /\.(mp4|webm)(\?|$)/i;
function isVideoUrl(url) {
  return VIDEO_EXT_RE.test(url || '');
}

/** `actionsLayout` inline põe trocar/remover na mesma linha — usado onde o
 *  bloco precisa ser baixo (o hero das configurações); empilhado é o padrão,
 *  para colunas estreitas como o modal de produto.
 *
 *  `shape="wide"` é o modo banner (~3:1, retangular) — ao lado de `square` e
 *  `circle`, que é tudo que a logo e as imagens de produto precisam. */
function ImageDropzone({ value, onChange, size = 88, shape = 'square', uploadFn, hint, actionsLayout = 'stacked' }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const isWide = shape === 'wide';
  const { upload, uploading, error } = useImageUpload(uploadFn, { acceptVideo: isWide });
  // No balcão, cadastrar produto é fotografar o prato na hora: a câmera merece
  // um botão próprio em vez de estar escondida atrás do seletor de arquivos do
  // sistema. No desktop não há câmera traseira para abrir, então some.
  const isTouch = useIsTouch();
  const previewIsVideo = isVideoUrl(value);
  const width = isWide ? size * 3 : size;

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const url = await upload(file);
    if (url) onChange(url);
  }

  return (
    <div>
      {error && (
        <Alert variant="danger" className="py-1 px-2 mb-2" style={{ fontSize: 12, borderRadius: 8 }}>
          {error}
        </Alert>
      )}
      <div className="d-flex align-items-center gap-3">
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          style={{
            width,
            height: size,
            borderRadius: shape === 'circle' ? '50%' : 12,
            border: value ? 'none' : '2px dashed #ddd',
            backgroundColor: '#fafafa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploading ? 'default' : 'pointer',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {uploading ? (
            <AppSpinner label="Enviando..." />
          ) : value && previewIsVideo ? (
            <video
              src={value}
              autoPlay
              loop
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : value ? (
            <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <IconImage size={26} style={{ color: '#ccc' }} />
          )}
        </div>

        <div className="d-flex flex-column align-items-start gap-1">
          {/* `flex-wrap` porque com a câmera são três botões numa linha: numa
              coluna estreita eles não cabem, e sem isto o navegador quebra o
              texto dentro do botão ("Tirar / foto") em vez de descer o botão
              inteiro para a linha de baixo. */}
          <div
            className={`image-actions d-flex gap-1 ${actionsLayout === 'inline' ? 'align-items-center flex-wrap' : 'flex-column align-items-start'}`}
          >
            {isTouch && (
              <AppButton
                type="button"
                variant="ghost"
                size="sm"
                disabled={uploading}
                onClick={() => cameraInputRef.current?.click()}
              >
                Tirar foto
              </AppButton>
            )}
            <AppButton
              type="button"
              variant="ghost"
              size="sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {isTouch ? 'Galeria' : value ? (isWide ? 'Trocar banner' : 'Trocar imagem') : 'Selecionar imagem'}
            </AppButton>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="action-btn action-btn-danger"
                style={{ padding: '2px 6px', fontSize: 12 }}
              >
                Remover
              </button>
            )}
          </div>
          <span style={{ fontSize: 11, color: '#aaa' }}>
            {hint || (isWide ? 'JPG, PNG, WEBP, GIF ou vídeo (MP4/WEBM) · até 5MB' : 'JPG, PNG, WEBP ou GIF · até 5MB')}
          </span>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={isWide ? ACCEPT_WIDE : ACCEPT}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* `capture="environment"` abre direto a câmera traseira. Precisa de um
          input separado: no mesmo elemento o atributo tiraria a opção de
          escolher da galeria. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
}

export default ImageDropzone;
